import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import TurndownService from 'turndown';
import '../styles/SummaryEditor.css';
import { SmartTemplateField } from '../types';
import { buildFieldBlock, buildFieldEndMarker, buildFieldStartMarker, createFieldBlockPattern, createFieldEndPattern, createFieldStartPattern } from '../utils/smartFieldMarkers';
import {
    buildColWidthComment,
    extractColWidthTokensByTable,
    hydrateColumnWidths,
    isColWidthComment,
    measureColWidthTokens,
} from '../utils/tableColumnWidths';

interface FieldOverlayBlock {
    id: string;
    start: number;
    end: number;
    code?: string;
    content: string;
    kind: 'field' | 'plain';
}

const FIELD_OVERLAY_COLORS = [
    { tint: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-500 text-white' },
    { tint: 'bg-sky-50 border-sky-200', badge: 'bg-sky-500 text-white' },
    { tint: 'bg-amber-50 border-amber-200', badge: 'bg-amber-500 text-white' },
    { tint: 'bg-rose-50 border-rose-200', badge: 'bg-rose-500 text-white' },
    { tint: 'bg-violet-50 border-violet-200', badge: 'bg-violet-500 text-white' },
    { tint: 'bg-cyan-50 border-cyan-200', badge: 'bg-cyan-500 text-white' },
];

const splitPlainBlocks = (text: string, baseStart: number): FieldOverlayBlock[] => {
    const blocks: FieldOverlayBlock[] = [];
    const separatorPattern = /\n{2,}/g;
    let cursor = 0;
    let blockIndex = 0;

    for (let match = separatorPattern.exec(text); match; match = separatorPattern.exec(text)) {
        const chunk = text.slice(cursor, match.index);
        if (chunk.trim()) {
            blocks.push({
                id: `plain-${baseStart + cursor}-${blockIndex}`,
                start: baseStart + cursor,
                end: baseStart + match.index,
                content: chunk,
                kind: 'plain',
            });
            blockIndex += 1;
        }
        cursor = match.index + match[0].length;
    }

    const tail = text.slice(cursor);
    if (tail.trim()) {
        blocks.push({
            id: `plain-${baseStart + cursor}-${blockIndex}`,
            start: baseStart + cursor,
            end: baseStart + text.length,
            content: tail,
            kind: 'plain',
        });
    }

    return blocks;
};

const parseFieldOverlayBlocks = (markdown: string): FieldOverlayBlock[] => {
    const blocks: FieldOverlayBlock[] = [];
    const fieldPattern = createFieldBlockPattern();
    let cursor = 0;

    for (let match = fieldPattern.exec(markdown); match; match = fieldPattern.exec(markdown)) {
        const fullMatch = match[0];
        const code = match[1] || match[2] || '';
        const matchStart = match.index;
        const matchEnd = matchStart + fullMatch.length;

        if (matchStart > cursor) {
            blocks.push(...splitPlainBlocks(markdown.slice(cursor, matchStart), cursor));
        }

        blocks.push({
            id: `field-${code}-${matchStart}`,
            start: matchStart,
            end: matchEnd,
            code,
            content: match[3] || '',
            kind: 'field',
        });

        cursor = matchEnd;
    }

    if (cursor < markdown.length) {
        blocks.push(...splitPlainBlocks(markdown.slice(cursor), cursor));
    }

    return blocks;
};

/**
 * Detecteer of platte tekst markdown-syntaxis bevat. Wordt gebruikt om bij plakken
 * te bepalen of we de tekst naar rich text (WYSIWYG) moeten converteren.
 */
const looksLikeMarkdown = (text: string): boolean => {
    const patterns = [
        /^#{1,6}\s/m,            // koppen (# Titel)
        /^\s*[-*+]\s+\S/m,       // ongeordende lijst
        /^\s*\d+\.\s+\S/m,       // geordende lijst
        /^\s*>\s/m,              // blockquote
        /\*\*[^*\n]+\*\*/,       // vet
        /__[^_\n]+__/,           // vet
        /\[[^\]\n]+\]\([^)\n]+\)/, // links
        /`[^`\n]+`/,             // inline code
        /^\s*\|.*\|.*$/m,        // tabel-rij
    ];
    return patterns.some((re) => re.test(text));
};

const getFieldColor = (code?: string, index = 0) => {
    if (!code) {
        return { tint: 'bg-gray-50 border-gray-200', badge: 'bg-gray-500 text-white' };
    }

    const charTotal = code.split('').reduce((total, character) => total + character.charCodeAt(0), 0);
    return FIELD_OVERLAY_COLORS[(charTotal + index) % FIELD_OVERLAY_COLORS.length];
};

interface MarkdownEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    minHeight?: string;
    rawMode?: boolean;
    smartFields?: SmartTemplateField[];
}

/**
 * A simple Tiptap-based WYSIWYG markdown editor.
 * Converts markdown to HTML for editing, and HTML back to markdown on change.
 */
const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
    value,
    onChange,
    placeholder,
    className = '',
    minHeight = '300px',
    rawMode = false,
    smartFields = [],
}) => {
    const { t } = useTranslation();
    const editorPlaceholder = placeholder ?? t('markdownEditor.defaultPlaceholder');
    // Refs to prevent infinite loops
    const isInternalUpdate = useRef(false);
    const lastValueRef = useRef(value);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [selectedFieldCode, setSelectedFieldCode] = useState('');
    // Kolombreedte-metadata (<!-- colwidth: ... -->): gemeten uit de live DOM
    // bij elke wijziging en per tabel-index uitgelezen door de turndown-regel.
    const measuredColWidthsRef = useRef<(string | null)[][]>([]);
    const colWidthTableCounterRef = useRef(0);
    // De hydratatie-transactie mag niet als gebruikerswijziging serialiseren.
    const isHydratingColWidthsRef = useRef(false);

    useEffect(() => {
        if (smartFields.length === 0) {
            setSelectedFieldCode('');
            return;
        }

        setSelectedFieldCode((currentCode) => {
            if (currentCode && smartFields.some((field) => field.code === currentCode)) {
                return currentCode;
            }

            return smartFields[0]?.code || '';
        });
    }, [smartFields]);

    const updateValueAtSelection = useCallback((
        updater: (params: {
            fullValue: string;
            selectionStart: number;
            selectionEnd: number;
            selectedText: string;
        }) => {
            nextValue: string;
            nextSelectionStart: number;
            nextSelectionEnd: number;
        }
    ) => {
        const textarea = textareaRef.current;
        const selectionStart = textarea?.selectionStart ?? value.length;
        const selectionEnd = textarea?.selectionEnd ?? value.length;
        const selectedText = value.slice(selectionStart, selectionEnd);
        const { nextValue, nextSelectionStart, nextSelectionEnd } = updater({
            fullValue: value,
            selectionStart,
            selectionEnd,
            selectedText,
        });

        onChange(nextValue);

        requestAnimationFrame(() => {
            if (!textareaRef.current) {
                return;
            }

            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(nextSelectionStart, nextSelectionEnd);
        });
    }, [onChange, value]);

    const insertFieldBlock = useCallback(() => {
        if (!selectedFieldCode) {
            return;
        }

        updateValueAtSelection(({ fullValue, selectionStart, selectionEnd }) => {
            const block = buildFieldBlock(selectedFieldCode);
            const needsLeadingNewline = selectionStart > 0 && fullValue[selectionStart - 1] !== '\n';
            const needsTrailingNewline = selectionEnd < fullValue.length && fullValue[selectionEnd] !== '\n';
            const prefix = needsLeadingNewline ? '\n' : '';
            const suffix = needsTrailingNewline ? '\n' : '';
            const insertValue = `${prefix}${block}${suffix}`;
            const nextValue = `${fullValue.slice(0, selectionStart)}${insertValue}${fullValue.slice(selectionEnd)}`;
            const cursorOffset = prefix.length + `${buildFieldStartMarker(selectedFieldCode)}\n`.length;
            const nextSelectionStart = selectionStart + cursorOffset;

            return {
                nextValue,
                nextSelectionStart,
                nextSelectionEnd: nextSelectionStart,
            };
        });
    }, [selectedFieldCode, updateValueAtSelection]);

    const wrapSelectionInField = useCallback(() => {
        if (!selectedFieldCode) {
            return;
        }

        updateValueAtSelection(({ fullValue, selectionStart, selectionEnd, selectedText }) => {
            const block = buildFieldBlock(selectedFieldCode, selectedText || '');
            const nextValue = `${fullValue.slice(0, selectionStart)}${block}${fullValue.slice(selectionEnd)}`;
            const bodyStart = selectionStart + `${buildFieldStartMarker(selectedFieldCode)}\n`.length;
            const bodyEnd = bodyStart + selectedText.length;

            return {
                nextValue,
                nextSelectionStart: bodyStart,
                nextSelectionEnd: bodyEnd,
            };
        });
    }, [selectedFieldCode, updateValueAtSelection]);

    const moveCurrentFieldBlock = useCallback(() => {
        if (!selectedFieldCode) {
            return;
        }

        const textarea = textareaRef.current;
        const cursor = textarea?.selectionStart ?? 0;
        const startPattern = createFieldStartPattern();
        let lastMatch: RegExpExecArray | null = null;

        for (let match = startPattern.exec(value); match; match = startPattern.exec(value)) {
            if (match.index > cursor) {
                break;
            }

            lastMatch = match;
        }

        if (!lastMatch) {
            return;
        }

        const currentCode = lastMatch[1] || lastMatch[2] || '';
        const startMarker = lastMatch[0];
        const endPattern = createFieldEndPattern(currentCode);
        const remainingValue = value.slice(lastMatch.index + startMarker.length);
        const endMatch = endPattern.exec(remainingValue);
        const endIndex = endMatch ? lastMatch.index + startMarker.length + endMatch.index : -1;

        if (endIndex === -1) {
            return;
        }

        const endMarker = endMatch?.[0] || buildFieldEndMarker(currentCode);
        const blockEnd = endIndex + endMarker.length;
        if (cursor < lastMatch.index || cursor > blockEnd) {
            return;
        }

        const nextValue = `${value.slice(0, lastMatch.index)}${buildFieldStartMarker(selectedFieldCode)}${value.slice(lastMatch.index + startMarker.length, endIndex)}${buildFieldEndMarker(selectedFieldCode)}${value.slice(blockEnd)}`;

        onChange(nextValue);

        requestAnimationFrame(() => {
            if (!textareaRef.current) {
                return;
            }

            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(cursor, cursor);
        });
    }, [onChange, selectedFieldCode, value]);

    const overlayBlocks = useMemo(() => rawMode ? parseFieldOverlayBlocks(value) : [], [rawMode, value]);

    const focusBlockInTextarea = useCallback((block: FieldOverlayBlock) => {
        requestAnimationFrame(() => {
            if (!textareaRef.current) {
                return;
            }

            textareaRef.current.focus();
            textareaRef.current.setSelectionRange(block.start, block.end);
        });
    }, []);

    const applyFieldToOverlayBlock = useCallback((block: FieldOverlayBlock, nextCode: string) => {
        const replacement = nextCode ? buildFieldBlock(nextCode, block.content) : block.content;
        const nextValue = `${value.slice(0, block.start)}${replacement}${value.slice(block.end)}`;
        onChange(nextValue);

        requestAnimationFrame(() => {
            if (!textareaRef.current) {
                return;
            }

            textareaRef.current.focus();
            const selectionEnd = block.start + replacement.length;
            textareaRef.current.setSelectionRange(block.start, selectionEnd);
        });
    }, [onChange, value]);

    // Turndown service for HTML to Markdown conversion - memoize to prevent recreating
    const turndownService = useMemo(() => {
        const service = new TurndownService({
            headingStyle: 'atx',
            codeBlockStyle: 'fenced',
            bulletListMarker: '-',
        });
        service.addRule('strikethrough', {
            filter: ['del', 's'] as ('del' | 's')[],
            replacement: (content) => `~~${content}~~`,
        });
        // Table support: convert HTML tables back to markdown tables
        service.addRule('tableCell', {
            filter: ['th', 'td'],
            replacement: (content) => ` ${content.trim().replace(/\n/g, ' ')} |`,
        });
        service.addRule('tableRow', {
            filter: 'tr',
            replacement: (content, node) => {
                const cells = content.trim();
                const row = `|${cells}`;
                // Add separator after header row
                const parent = node.parentNode;
                if (parent && parent.nodeName === 'THEAD') {
                    const cellCount = node.childNodes.length;
                    const separator = '|' + Array.from({ length: cellCount }, () => ' --- |').join('');
                    return `${row}\n${separator}\n`;
                }
                return `${row}\n`;
            },
        });
        service.addRule('table', {
            filter: 'table',
            replacement: (content) => {
                // Kolombreedtes komen uit de live DOM-meting (measureColWidthTokens),
                // gekoppeld per tabel-index in documentvolgorde. Set-kolommen zijn een
                // percentage van de tabelbreedte; niet-aangepaste kolommen zijn `null`.
                const measuredWidths = measuredColWidthsRef.current[colWidthTableCounterRef.current] || null;
                colWidthTableCounterRef.current += 1;

                const colWidthComment = measuredWidths ? buildColWidthComment(measuredWidths) : null;
                const prefix = colWidthComment ? `\n${colWidthComment}\n` : '\n';
                return `${prefix}${content.trim()}\n\n`;
            },
        });
        service.addRule('tableSection', {
            filter: ['thead', 'tbody', 'tfoot'],
            replacement: (content) => content,
        });
        // Disable escaping of special characters (brackets, asterisks, etc.)
        // This prevents \[, \*, \_, etc. from appearing in the output
        (service as any).escape = (text: string) => text;
        return service;
    }, []);

    /**
     * Convert markdown to HTML for Tiptap
     */
    const markdownToHtml = useCallback((markdown: string): string => {
        if (!markdown) return '<p></p>';

        const processInlineMarkdown = (text: string) => {
            return text
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/__([^_]+?)__/g, '<strong>$1</strong>')
                .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
                .replace(/_([^_]+?)_/g, '<em>$1</em>')
                .replace(/`([^`]+?)`/g, '<code>$1</code>')
                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
        };

        const lines = markdown.split('\n');
        let html = '';
        let inList = false;
        let listType = '';
        let inTable = false;
        let tableRows: string[] = [];

        const closeList = () => {
            if (inList) {
                html += listType === 'ul' ? '</ul>' : '</ol>';
                inList = false;
                listType = '';
            }
        };

        const closeTable = () => {
            if (inTable && tableRows.length > 0) {
                html += '<table>';
                const headerCells = tableRows[0].split('|').map(c => c.trim()).filter(c => c !== '');
                html += '<thead><tr>';
                headerCells.forEach(cell => { html += `<th>${processInlineMarkdown(cell)}</th>`; });
                html += '</tr></thead>';
                if (tableRows.length > 1) {
                    html += '<tbody>';
                    const startIdx = tableRows[1].includes('---') ? 2 : 1;
                    for (let j = startIdx; j < tableRows.length; j++) {
                        const cells = tableRows[j].split('|').map(c => c.trim()).filter(c => c !== '');
                        if (cells.length > 0) {
                            html += '<tr>';
                            html += cells.map(cell => `<td>${processInlineMarkdown(cell)}</td>`).join('');
                            html += '</tr>';
                        }
                    }
                    html += '</tbody>';
                }
                html += '</table>';
            }
            tableRows = [];
            inTable = false;
        };

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const trimmed = line.trim();

            // Skip de colwidth metadata-comment; de breedtes worden na het laden
            // los van de HTML gehydrateerd (hydrateColumnWidths).
            if (isColWidthComment(trimmed)) {
                continue;
            }

            // Table rows
            const isTableRow = trimmed.includes('|') && trimmed.split('|').length > 2;
            const isTableSeparator = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(trimmed);

            if (isTableRow || isTableSeparator) {
                if (!inTable) {
                    closeList();
                    inTable = true;
                }
                if (!isTableSeparator) {
                    tableRows.push(trimmed);
                }
                continue;
            } else if (inTable) {
                closeTable();
            }

            // Headers
            const headerMatch = line.match(/^(#{1,6})\s+(.*)$/);
            if (headerMatch) {
                closeList();
                const level = headerMatch[1].length;
                html += `<h${level}>${processInlineMarkdown(headerMatch[2])}</h${level}>`;
                continue;
            }

            // Unordered list
            const ulMatch = line.match(/^(\s*)[*-]\s+(.*)$/);
            if (ulMatch) {
                if (!inList || listType !== 'ul') {
                    closeList();
                    html += '<ul>';
                    inList = true;
                    listType = 'ul';
                }
                html += `<li>${processInlineMarkdown(ulMatch[2])}</li>`;
                continue;
            }

            // Ordered list
            const olMatch = line.match(/^(\s*)\d+\.\s+(.*)$/);
            if (olMatch) {
                if (!inList || listType !== 'ol') {
                    closeList();
                    html += '<ol>';
                    inList = true;
                    listType = 'ol';
                }
                html += `<li>${processInlineMarkdown(olMatch[2])}</li>`;
                continue;
            }

            // Empty line
            if (trimmed === '') {
                continue;
            }

            // Regular paragraph
            closeList();
            html += `<p>${processInlineMarkdown(line)}</p>`;
        }

        closeList();
        closeTable();
        return html || '<p></p>';
    }, []);

    /**
     * Convert HTML to markdown using Turndown
     */
    const htmlToMarkdown = useCallback((html: string): string => {
        if (!html) return '';

        // Clean up empty paragraphs
        const cleanHtml = html.replace(/<p><\/p>/g, '');
        if (!cleanHtml.trim()) return '';

        // De turndown-tabelregel telt per aanroep de tabellen af tegen de
        // gemeten kolombreedtes; begin elke conversie bij tabel 0.
        colWidthTableCounterRef.current = 0;
        return turndownService.turndown(cleanHtml);
    }, [turndownService]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4, 5, 6],
                },
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-primary-600 underline hover:text-primary-700',
                },
            }),
            Placeholder.configure({
                placeholder: editorPlaceholder,
                emptyEditorClass: 'is-editor-empty',
            }),
            // Native resizable tabellen: Tiptap beheert kolombreedtes als px op
            // de cellen. De %-waarden worden bij laden/opslaan geconverteerd
            // (hydrateColumnWidths / measureColWidthTokens).
            Table.configure({
                resizable: true,
                HTMLAttributes: {
                    class: 'markdown-table',
                },
            }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: markdownToHtml(value),
        editorProps: {
            attributes: {
                // Gebruik dezelfde typografie/heading-groottes als SummaryEditor
                // (gedefinieerd onder `.notion-editor` in SummaryEditor.css).
                class: 'notion-editor focus:outline-none',
            },
            handlePaste: (_view, event) => {
                const clipboard = event.clipboardData;
                if (!clipboard) {
                    return false;
                }

                const plainText = clipboard.getData('text/plain');
                if (!plainText || !looksLikeMarkdown(plainText)) {
                    // Geen markdown: laat Tiptap de paste (incl. rich HTML) zelf afhandelen.
                    return false;
                }

                // Platte-tekst markdown: converteer naar HTML zodat het als WYSIWYG verschijnt.
                event.preventDefault();
                const html = markdownToHtml(plainText);
                editor?.commands.insertContent(html);
                return true;
            },
        },
        onUpdate: ({ editor }) => {
            // De colwidth-hydratatie is geen gebruikerswijziging; niet serialiseren.
            if (isHydratingColWidthsRef.current) {
                return;
            }

            // Mark as internal update to prevent loop
            isInternalUpdate.current = true;

            // Meet de actuele kolombreedtes (% van de tabelbreedte) uit de live
            // DOM; de turndown-tabelregel leest deze per tabel-index uit.
            measuredColWidthsRef.current = measureColWidthTokens(editor);

            const html = editor.getHTML();
            const markdown = htmlToMarkdown(html);
            lastValueRef.current = markdown;
            onChange(markdown);
            // Reset the flag after a short delay
            setTimeout(() => {
                isInternalUpdate.current = false;
            }, 0);
        },
    });

    // Hydrateer de kolombreedtes uit de colwidth-comments in de markdown, zodra
    // de tabellen gerenderd zijn (de %-waarden hebben de tabelbreedte nodig).
    const hydrateColWidthsFromMarkdown = useCallback((markdown: string) => {
        if (!editor) {
            return;
        }

        const tokensByTable = extractColWidthTokensByTable(markdown);
        if (!tokensByTable.some((tokens) => tokens.length > 0)) {
            return;
        }

        setTimeout(() => {
            if (!editor || editor.isDestroyed) {
                return;
            }

            isHydratingColWidthsRef.current = true;
            try {
                hydrateColumnWidths(editor, tokensByTable);
            } finally {
                isHydratingColWidthsRef.current = false;
            }
        }, 200);
    }, [editor]);

    // Hydrateer de initiële content zodra de editor bestaat.
    const hasHydratedInitialColWidthsRef = useRef(false);
    useEffect(() => {
        if (!editor || hasHydratedInitialColWidthsRef.current) {
            return;
        }

        hasHydratedInitialColWidthsRef.current = true;
        hydrateColWidthsFromMarkdown(lastValueRef.current);
    }, [editor, hydrateColWidthsFromMarkdown]);

    // Update editor content when value prop changes externally
    useEffect(() => {
        // Skip if this is an internal update (from onUpdate callback)
        if (isInternalUpdate.current) {
            return;
        }

        // Skip if value hasn't actually changed
        if (value === lastValueRef.current) {
            return;
        }

        if (editor) {
            lastValueRef.current = value;
            const html = markdownToHtml(value);
            editor.commands.setContent(html);
            hydrateColWidthsFromMarkdown(value);
        }
    }, [value, editor, markdownToHtml, hydrateColWidthsFromMarkdown]);

    if (rawMode) {
        return (
            <div className={`markdown-editor border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white ${className}`}>
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="min-w-[220px] flex-1">
                            <label className="sr-only" htmlFor="smart-field-select">Smart field</label>
                            <select
                                id="smart-field-select"
                                value={selectedFieldCode}
                                onChange={(event) => setSelectedFieldCode(event.target.value)}
                                disabled={smartFields.length === 0}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                {smartFields.length === 0 ? (
                                    <option value="">{t('markdownEditor.smartField.addFirst')}</option>
                                ) : (
                                    smartFields.map((field) => (
                                        <option key={field.code} value={field.code}>
                                            {field.label || field.code} ({field.code})
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={insertFieldBlock}
                            disabled={!selectedFieldCode}
                            className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Insert field block
                        </button>
                        <button
                            type="button"
                            onClick={wrapSelectionInField}
                            disabled={!selectedFieldCode}
                            className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Wrap selection in field
                        </button>
                        <button
                            type="button"
                            onClick={moveCurrentFieldBlock}
                            disabled={!selectedFieldCode}
                            className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Move field block
                        </button>
                    </div>
                    <p className="text-xs text-gray-500">
                        Deze editor werkt op ruwe markdown met expliciete field markers. De viewer gebruikt later automatisch de opgeschoonde `content`.
                    </p>
                </div>

                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    placeholder={editorPlaceholder}
                    className="w-full px-4 py-4 font-mono text-sm leading-6 text-gray-900 bg-white border-0 focus:outline-none resize-y"
                    style={{ minHeight }}
                    spellCheck={false}
                />

                <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900">Smart field overlay</h4>
                            <p className="text-xs text-gray-500 mt-1">
                                Bepaal eerst je templatecontent. Koppel daarna visueel blokken aan een veld; de markers worden automatisch in de markdown geschreven.
                            </p>
                        </div>
                        <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-600 border border-gray-200">
                            {overlayBlocks.length} blok{overlayBlocks.length === 1 ? '' : 'ken'}
                        </span>
                    </div>

                    {overlayBlocks.length > 0 ? (
                        <div className="space-y-3">
                            {overlayBlocks.map((block, index) => {
                                const color = getFieldColor(block.code, index);
                                const assignedField = block.code ? smartFields.find((field) => field.code === block.code) : undefined;

                                return (
                                    <div
                                        key={block.id}
                                        className={`rounded-xl border px-4 py-4 shadow-sm transition-colors ${color.tint}`}
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${color.badge}`}>
                                                        {assignedField ? assignedField.label || assignedField.code : 'Niet gekoppeld'}
                                                    </span>
                                                    {block.code && (
                                                        <span className="text-xs text-gray-500">{block.code}</span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => focusBlockInTextarea(block)}
                                                    className="text-left w-full"
                                                >
                                                    <div
                                                        key={block.content}
                                                        className="prose prose-sm max-w-none text-gray-800"
                                                        dangerouslySetInnerHTML={{ __html: markdownToHtml(block.content.trim()) }}
                                                    />
                                                </button>
                                            </div>

                                            <div className="w-full md:w-72 shrink-0 space-y-2">
                                                <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                                                    Dataveld
                                                </label>
                                                <select
                                                    value={block.code || ''}
                                                    onChange={(event) => applyFieldToOverlayBlock(block, event.target.value)}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                >
                                                    <option value="">{t('markdownEditor.smartField.noLink')}</option>
                                                    {smartFields.map((field) => (
                                                        <option key={field.code} value={field.code}>
                                                            {field.label || field.code} ({field.code})
                                                        </option>
                                                    ))}
                                                </select>
                                                <p className="text-xs text-gray-500">
                                                    Klik op het blok om de corresponderende markdown te selecteren.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500">
                            {t('markdownEditor.smartField.emptyHint')}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!editor) {
        return (
            <div className="animate-pulse bg-gray-100 rounded-xl" style={{ minHeight }}>
                <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
            </div>
        );
    }

    return (
        <div className={`markdown-editor border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent transition-all ${className}`}>
            {/* Toolbar */}
            <div className="flex items-center gap-0.5 px-3 py-2 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white">
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`p-2 rounded-lg hover:bg-gray-100 transition-all duration-150 ${editor.isActive('bold') ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('markdownEditor.toolbar.bold')}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
                    </svg>
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded-lg hover:bg-gray-100 transition-all duration-150 ${editor.isActive('italic') ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('markdownEditor.toolbar.italic')}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h4m-2 0v16m-4 0h8" />
                    </svg>
                </button>

                <div className="w-px h-5 bg-gray-200 mx-2"></div>

                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    className={`px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-all duration-150 ${editor.isActive('heading', { level: 1 }) ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('markdownEditor.toolbar.heading1')}
                >
                    <span className="text-xs font-bold">H1</span>
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-all duration-150 ${editor.isActive('heading', { level: 2 }) ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('markdownEditor.toolbar.heading2')}
                >
                    <span className="text-xs font-bold">H2</span>
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={`px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-all duration-150 ${editor.isActive('heading', { level: 3 }) ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('markdownEditor.toolbar.heading3')}
                >
                    <span className="text-xs font-bold">H3</span>
                </button>

                <div className="w-px h-5 bg-gray-200 mx-2"></div>

                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded-lg hover:bg-gray-100 transition-all duration-150 ${editor.isActive('bulletList') ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('markdownEditor.toolbar.bulletList')}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <circle cx="4" cy="6" r="1.5" fill="currentColor" />
                        <circle cx="4" cy="12" r="1.5" fill="currentColor" />
                        <circle cx="4" cy="18" r="1.5" fill="currentColor" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h12M8 12h12M8 18h12" />
                    </svg>
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded-lg hover:bg-gray-100 transition-all duration-150 ${editor.isActive('orderedList') ? 'bg-primary-50 text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    title={t('markdownEditor.toolbar.orderedList')}
                >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <text x="2" y="8" fontSize="7" fontWeight="bold">1</text>
                        <text x="2" y="14" fontSize="7" fontWeight="bold">2</text>
                        <text x="2" y="20" fontSize="7" fontWeight="bold">3</text>
                        <line x1="9" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <line x1="9" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <line x1="9" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
            </div>

            {/* Bubble menu for formatting selected text */}
            <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }}>
                <div className="flex items-center gap-1 bg-gray-900 rounded-lg shadow-xl p-1.5 border border-gray-700">
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={`p-1.5 rounded-md text-white hover:bg-gray-700 transition-colors ${editor.isActive('bold') ? 'bg-gray-700' : ''}`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={`p-1.5 rounded-md text-white hover:bg-gray-700 transition-colors ${editor.isActive('italic') ? 'bg-gray-700' : ''}`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h4m-2 0v16m-4 0h8" />
                        </svg>
                    </button>
                </div>
            </BubbleMenu>

            {/* Editor content */}
            <div className="bg-white px-5 py-4" style={{ minHeight }}>
                <EditorContent editor={editor} className="summary-editor-content" />
            </div>
        </div>
    );
};

export default MarkdownEditor;
